import Flex from '@Styles/flex.scss';
import { CircularProgress } from '@mui/material';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Autocomplete } from '@Components/DomElements/Autocomplete';
import TextInput from '@Components/DomElements/TextInput';
import {
    ModalBody,
    ModalFooter,
    ModalHeader,
} from '@Components/ModalComponents';
import { QualityControlApi } from '@Data/qualityControl/Api';
import {
    IReworkReason,
    IReworkReasonStage,
    IReworkReasonTopic,
} from '@Data/qualityControl/Interfaces';
import {
    allReworkReasonsState,
    allReworkTopicsState,
    reworkStagesState,
} from '@Data/qualityControl/Reducer';
import Styles from './ManageReworkDialog.scss';
import { ReworkOptionGrid } from './ReworkOptionGrid';

interface IManageReworkDialogProps {
    onClose: () => void;
    selectedReason?: IReworkReason;
    onSubmit: (reason: IReworkReason, comment: string) => void;
}

export type ReworkColumn = 'stage' | 'topic' | 'reason';

function getStageAndTopicFromReason(
    stages: IReworkReasonStage[],
    topics: IReworkReasonTopic[],
    reason: IReworkReason
): {
    stage: IReworkReasonStage;
    topic: IReworkReasonTopic;
} {
    const selectedTopic = topics.find((t) => t.id === reason.fk_topic);
    if (_isNil(selectedTopic)) return null;

    const selectedStage = stages.find((r) => r.id === selectedTopic.fk_stage);
    if (_isNil(selectedStage)) return null;

    return {
        stage: selectedStage,
        topic: selectedTopic,
    };
}

const ManageReworkDialog = (props: IManageReworkDialogProps) => {
    const { onClose, selectedReason } = props;

    const reworkStages = useSelector(reworkStagesState);
    const reworkReasons = useSelector(allReworkReasonsState);
    const reworkTopics = useSelector(allReworkTopicsState);

    useEffect(() => {
        if (_isNil(reworkStages) || reworkStages.length === 0) {
            QualityControlApi.getReworkStageTopicReasons();
        }
    }, [reworkStages]);

    const [selectedColumn, setSelectedColumn] = useState<ReworkColumn>('stage');

    const [selectedStage, setSelectedStage] =
        useState<IReworkReasonStage>(null);
    const [selectedTopic, setSelectedTopic] =
        useState<IReworkReasonTopic>(null);

    const [selectedReworkReason, setSelectedReworkReason] =
        useState<IReworkReason>(null);

    const handleSelectStage = (stage: IReworkReasonStage) => {
        if (stage !== selectedStage) {
            setSelectedTopic(null);
            setSelectedReworkReason(null);
        }
        setSelectedStage(stage);
        setSelectedColumn('topic');
    };

    const handleSelectTopic = (topic: IReworkReasonTopic) => {
        if (topic !== selectedTopic) {
            setSelectedReworkReason(null);
        }
        setSelectedTopic(topic);
        setSelectedColumn('reason');
    };

    const textAreaId = 'reworkTextArea';

    const setTextAreaValue = (value: string) => {
        (document.getElementById(textAreaId) as HTMLTextAreaElement).value =
            value;
    };

    const setStageAndTopicFromReason = (
        reason: IReworkReason,
        setComment = false
    ) => {
        const existingRr = reworkReasons.find((r) => r.id === reason.id);

        const stageAndTopic = getStageAndTopicFromReason(
            reworkStages,
            reworkTopics,
            existingRr
        );
        if (!_isNil(stageAndTopic)) {
            setSelectedStage(stageAndTopic.stage);
            setSelectedTopic(stageAndTopic.topic);
            setSelectedReworkReason(existingRr);

            setSelectedColumn('reason');

            if (setComment) {
                setTextAreaValue(reason.comment);
            }
        }
    };

    useEffect(() => {
        if (!_isNil(selectedReason) && !_isNil(reworkReasons)) {
            setStageAndTopicFromReason(selectedReason, true);
        }
    }, [selectedReason, reworkReasons, reworkStages, reworkTopics]);

    const addReworkReasonToEntity = async () => {
        const comment = (
            document.getElementById(textAreaId) as HTMLTextAreaElement
        ).value;
        await props.onSubmit(selectedReworkReason, comment);
        handleClose();
    };

    const height =
        document.getElementById('lowerPanelsContainer')?.clientHeight ?? 'auto';

    const handleClose = () => {
        setSelectedReworkReason(null);
        setTextAreaValue('');
        onClose();
    };

    const title = `${!_isNil(selectedReason) ? 'Edit' : 'Add'} Rework Reason`;

    const autocompleteReasons = useMemo(() => {
        return reworkReasons?.map((r) => {
            const stageAndTopic = getStageAndTopicFromReason(
                reworkStages,
                reworkTopics,
                r
            );
            return {
                id: r.id,
                label:
                    stageAndTopic.stage.description +
                    '>' +
                    stageAndTopic.topic.description +
                    '>' +
                    r.description,
            };
        });
    }, [reworkReasons]);

    const handleAutocompleteSelection = (e, value) => {
        setStageAndTopicFromReason(value);
    };

    const getFilterOptions = (
        options: Array<any>,
        state: { inputValue: string }
    ) => {
        if (state.inputValue.length === 0) {
            return [];
        }
        return options.filter((o) => o.label.includes(state.inputValue));
    };

    const innerHtml =
        _isNil(reworkStages) || reworkStages.length === 0 ? (
            <ModalBody
                className={cx(
                    Flex.flex,
                    Flex.alignItemsCenter,
                    Flex.grow,
                    Flex.justifyCenter
                )}
            >
                <CircularProgress />
            </ModalBody>
        ) : (
            <>
                <ModalBody className={Styles.modalBody}>
                    <Autocomplete
                        options={autocompleteReasons}
                        onChange={handleAutocompleteSelection}
                        value={null}
                        placeholder="Filter rework reasons"
                        filterOptions={getFilterOptions}
                        noOptionsText={
                            autocompleteReasons.length > 0
                                ? 'Start typing to view results'
                                : 'No rework reasons found'
                        }
                    />
                    <ReworkOptionGrid
                        selectedColumn={selectedColumn}
                        onSelectColumn={(col) => setSelectedColumn(col)}
                        stages={reworkStages}
                        selectedReason={selectedReworkReason}
                        onSelectReason={(r) => setSelectedReworkReason(r)}
                        selectedStage={selectedStage}
                        onSelectStage={handleSelectStage}
                        selectedTopic={selectedTopic}
                        onSelectTopic={handleSelectTopic}
                    />
                    <TextInput
                        textArea={true}
                        rows={2}
                        label="Comments"
                        isEditing
                        id={textAreaId}
                    />
                </ModalBody>
                <ModalFooter
                    id="manage-rework-footer"
                    onCancel={handleClose}
                    canSubmit={!_isNil(selectedReworkReason)}
                    onSubmit={addReworkReasonToEntity}
                    submitText={'Save'}
                />
            </>
        );

    return (
        <div
            id="manage-rework-dialog"
            style={{
                height: height,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}
        >
            <ModalHeader id="manage-rework-header" title={title} />
            {innerHtml}
        </div>
    );
};

export default ManageReworkDialog;
